// Pure display helpers for the Strategy → Prospects tab. Framework-free (no Vue).
import type { ShortlistFund, ShortlistGate, LevyCheckSummaryRow } from '../../../api/client'
import { pctYear } from '../../edge/edgeSourceFormat'

const DASH = '—'

/** toFixed without a negative zero ("-0.0"). */
function fixed(n: number, digits: number): string {
  const s = n.toFixed(digits)
  return /^-0(\.0+)?$/.test(s) ? s.slice(1) : s
}

/** Finite number, or a numeric string; "NaN"/"Infinity"/anything else → null. */
export function num(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string') {
    const t = v.trim()
    if (t === '') return null
    const n = Number(t)
    return Number.isFinite(n) ? n : null
  }
  return null
}

export function fmt(v: unknown, digits = 1, suffix = ''): string {
  const n = num(v)
  return n === null ? DASH : fixed(n, digits) + suffix
}

export function extraPct(f: ShortlistFund): number | null {
  const r = num(f.ruleCagrPct), b = num(f.bhCagrPct)
  return r === null || b === null ? null : r - b
}

/** "name 0.93": the closest pot fund by signal correlation; the name comes from nameOf (a
 * shortlist row's fund name) and falls back to the fund key. */
export function closestPotText(f: ShortlistFund, nameOf: (key: string) => string | null | undefined = () => null): string {
  const key = f.closestPotFund
  if (!key) return DASH
  const name = nameOf(key) || key
  const corr = num(f.closestPotCorr)
  return corr === null ? name : `${name} ${fixed(corr, 2)}`
}

export function thumbCagrPct(f: ShortlistFund): number | null {
  const bp = num(f.edgeBpPerDay)
  return bp === null ? null : 2 * bp
}

export const FLAG_ORDER: readonly string[] = [
  'short-history', 'fx-driven', 're-map?', 'cutoff?', 'levy', 'foreign-days', 'ocf>1%', 'small', 'ex-div?', 'n/a', 'coarse-nav',
]

export const FLAG_LEGEND: Record<string, string> = {
  'short-history': 'Fewer than 500 (ShortHistoryDays) matched NAV days, so the scores are not comparable with full-history funds.',
  'fx-driven': 'Local-currency edge left differs from the GBP edge left by more than 10 points (FxDrivenGapPts): the edge is FX-driven.',
  're-map?': 'The alternative future correlates better than the mapped one by more than 0.05 (RemapCorrMargin); the future mapping may be wrong.',
  'cutoff?': 'The dealing cutoff for this fund is not confirmed.',
  'levy': 'The prospectus allows a dilution levy or similar pricing adjustment.',
  'foreign-days': 'The fund is priced only on foreign trading days.',
  'ocf>1%': 'Ongoing charge above 1% (HighOcfPct).',
  'small': 'Fund size is under £100m (SmallFundGbpM).',
  'ex-div?': 'Ex-dividend dates are unknown, so NAV drops cannot be separated from signal.',
  'n/a': 'Pass-through is below the floor, so edge left and expected edge are not defined.',
  'coarse-nav': 'The unchanged-NAV share is above 10% (CoarseNavShare); the NAV is stale or rounded on many days.',
  'score-error': 'The scorer failed for this fund on the last run.',
  'facts-unreadable': 'The stored fund facts could not be parsed.',
}

export function orderedFlags(flags: string[]): string[] {
  const known = FLAG_ORDER.filter(f => flags.includes(f))
  const rest = flags.filter(f => !FLAG_ORDER.includes(f))
  return [...known, ...rest]
}

export type SortKey = 'rank' | 'fundName' | 'sector' | 'edgeBpPerDay' | 'tStatNext' | 'edgeLeftPct' | 'passthroughPct' | 'expectedEdgePct' | 'ruleCagrPct' | 'bhCagrPct' | 'extra' | 'thumb' | 'betaSame' | 'betaNext' | 'edgeLeft250dPct' | 'corrNext' | 'nDays' | 'localCcyEdgeLeftPct' | 'altExpectedEdgePct'
  | 'stale250Bp' | 'stale120Bp' | 'continuation250Bp' | 'drift250Bp' | 'payoff250Bp'
  | 'potScore250' | 'edgeSharpe250' | 'signalCorrPot250'

function sortValue(f: ShortlistFund, key: SortKey): number | string | null {
  if (key === 'extra') return extraPct(f)
  if (key === 'thumb') return thumbCagrPct(f)
  if (key === 'fundName') return f.fundName
  if (key === 'sector') return f.sector
  return num(f[key])
}

/** Stable sort; null values always last regardless of direction; strings by localeCompare. */
export function sortFunds(funds: ShortlistFund[], key: SortKey, dir: 'asc' | 'desc'): ShortlistFund[] {
  const sign = dir === 'asc' ? 1 : -1
  return funds
    .map((f, i) => ({ f, i, v: sortValue(f, key) }))
    .sort((a, b) => {
      if (a.v === null && b.v === null) return a.i - b.i
      if (a.v === null) return 1
      if (b.v === null) return -1
      const c = typeof a.v === 'string' || typeof b.v === 'string'
        ? String(a.v).localeCompare(String(b.v))
        : a.v - (b.v as number)
      return c !== 0 ? sign * c : a.i - b.i
    })
    .map(x => x.f)
}

export interface MeasureRow { label: string; value: string; hint?: string }
export interface MeasureTable { title: string; columns: string[]; rows: string[][] }
export interface MeasureGroup { key: 'A' | 'B' | 'C' | 'D' | 'E' | 'F' | 'G'; title: string; rows: MeasureRow[]; tables?: MeasureTable[] }
export interface LiveContext { pricingRag?: { rag: string; reason: string | null } | null; levy?: LevyCheckSummaryRow | null; gate?: ShortlistGate | null }

type Rec = Record<string, unknown>
const rec = (v: unknown): Rec | null => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Rec) : null)
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : [])
const recs = (v: unknown): Rec[] => arr(v).map(rec).filter((r): r is Rec => r !== null)
const text = (v: unknown): string => (v === null || v === undefined || v === '' ? DASH : String(v))
const pct1 = (v: unknown) => fmt(v, 1, '%')
const share = (v: unknown) => { const n = num(v); return n === null ? DASH : fixed(n * 100, 1) + '%' }
/** part / total as a percentage; "—" unless total is a positive finite number. */
const ratioShare = (part: unknown, total: unknown) => {
  const p = num(part), t = num(total)
  return p === null || t === null || t <= 0 ? DASH : fixed((p / t) * 100, 1) + '%'
}
const bp = (v: unknown) => { const n = num(v); return n === null ? DASH : fixed(n * 1e4, 1) }
const int = (v: unknown) => fmt(v, 0)
const bool = (v: unknown) => (typeof v === 'boolean' ? (v ? 'yes' : 'no') : DASH)

function scalarOrJson(v: unknown): { value: string; hint?: string } {
  if (v === null || v === undefined || v === '') return { value: DASH }
  if (typeof v === 'object') {
    // Never truncated here: the view collapses long values into <details> instead.
    return { value: JSON.stringify(v) }
  }
  return { value: String(v) }
}

/** A value shown by a dedicated row as a plain scalar (not empty, not an object). */
const shownScalar = (v: unknown): boolean => v !== null && v !== undefined && v !== '' && typeof v !== 'object'

/** Gate lines to show: failures plus Japan failures (which live in their own field). */
export function gateLines(gate: ShortlistGate | null | undefined): string[] {
  return gate ? [...gate.failures, ...(gate.japanFailures ?? [])] : []
}

export function productionFund(f: ShortlistFund): string | null {
  const v = rec(rec(f.facts)?.production_ref)?.fund
  return typeof v === 'string' && v ? v : null
}

const SEGMENT_NAMES = ['const', 'fut after-strike', 'fut overnight', 'fut 08:40→strike', 'fx after-strike', 'fx overnight', 'fx 08:40→strike']

export const MEASURE_LABELS: Record<MeasureGroup['key'], readonly string[]> = {
  A: ['Key', 'Fund', 'SEDOL', 'ISIN', 'Share class', 'Future', 'Valuation time', 'Status', 'Status reason', 'Notes',
    'Pass-through %', 'Edge left %', 'Expected edge %', 'Edge bp/day',
    'Pot score', 'Edge Sharpe 250d', 'Signal corr. with yardstick', 'Closest yardstick fund', 'Stale 250d %/yr', 'Stale 120d %/yr', 'Continuation 250d', 'Drift 250d', 'Payoff 250d', 'Rule CAGR %', 'B&H CAGR %', 'Extra (pts)',
    'Rule of thumb (2×bp/day %)', 'Edge left 250d %', 't (next)', 'Corr (next)', 'N days', 'Local-ccy edge left %',
    'Alt future', 'Alt expected edge %', 'Alt corr (next)', 'β same', 'β next', 'Drops: bad print', 'Drops: ex-div',
    'Drops: repeat', 'Drops: no price', 'Unchanged NAV share', 'As of'],
  B: [],
  C: ['Size £m', 'OCF %', 'Levy', 'Fair value', 'HL page', 'Production ref'],
  D: ['Cross-source common', 'Cross-source only-primary', 'Cross-source only-secondary', 'Cross-source scale', 'Cross-source max rel gap',
    'Cross-source gaps >0.5%', 'NAV points', 'Unchanged share', 'Long gaps', 'Largest move (log return)', 'Rule total (log-return sum)', 'Top-10 days (log-return sum)', 'Top-50 days (log-return sum)',
    'Top-10 share of rule total', 'Top-50 share of rule total',
    'Days in', 'Segment R²', 'Segment N', 'Segment error'],
  E: ['Pricing RAG', 'Reason', 'β20', 'β40', 'β120', 'β250', 'Edge left 40', 'Edge left 250', 'Green ceiling',
    'Frozen ceiling', 'Ceiling frozen', 'Red floor', 'Plausible pairs', 'Latest date', 'Ceiling set at', 'Gate', 'Not rankable'],
  F: ['Production RAG', 'Levy checks'],
  G: ['Status', 'Status reason'],
}

/** <code>nameOf</code> maps a shortlist fund key to its fund name (the closest pot fund row); unknown keys show as keys. */
export function measureGroups(f: ShortlistFund, live: LiveContext = {}, nameOf?: (key: string) => string | null | undefined): MeasureGroup[] {
  const facts = rec(f.facts)
  const drops = rec(f.drops)
  const audit = rec(f.audit)
  const ps = rec(f.pricingSource)
  const isLive = f.status === 'live'

  const A: MeasureRow[] = [
    { label: 'Key', value: f.fundKey },
    { label: 'Fund', value: f.fundName },
    { label: 'SEDOL', value: text(f.sedol) },
    { label: 'ISIN', value: text(f.isin) },
    { label: 'Share class', value: text(f.shareClass) },
    { label: 'Future', value: text(f.future) },
    { label: 'Valuation time', value: text(f.valuationTimeUk) },
    { label: 'Status', value: f.status },
    { label: 'Status reason', value: text(f.statusReason) },
    { label: 'Notes', ...scalarOrJson(facts?.notes) },
    { label: 'Pass-through %', value: pct1(f.passthroughPct) },
    { label: 'Edge left %', value: pct1(f.edgeLeftPct) },
    { label: 'Expected edge %', value: pct1(f.expectedEdgePct) },
    { label: 'Edge bp/day', value: fmt(f.edgeBpPerDay, 1) },
    { label: 'Pot score', value: fmt(f.potScore250, 2) },
    { label: 'Edge Sharpe 250d', value: fmt(f.edgeSharpe250, 2) },
    { label: 'Signal corr. with yardstick', value: fmt(f.signalCorrPot250, 2) },
    { label: 'Closest yardstick fund', value: closestPotText(f, nameOf) },
    { label: 'Stale 250d %/yr', value: pctYear(num(f.stale250Bp)) },
    { label: 'Stale 120d %/yr', value: pctYear(num(f.stale120Bp)) },
    { label: 'Continuation 250d', value: pctYear(num(f.continuation250Bp)) },
    { label: 'Drift 250d', value: pctYear(num(f.drift250Bp)) },
    { label: 'Payoff 250d', value: pctYear(num(f.payoff250Bp)) },
    { label: 'Rule CAGR %', value: pct1(f.ruleCagrPct) },
    { label: 'B&H CAGR %', value: pct1(f.bhCagrPct) },
    { label: 'Extra (pts)', value: fmt(extraPct(f), 1) },
    { label: 'Rule of thumb (2×bp/day %)', value: pct1(thumbCagrPct(f)) },
    { label: 'Edge left 250d %', value: pct1(f.edgeLeft250dPct) },
    { label: 't (next)', value: fmt(f.tStatNext, 2) },
    { label: 'Corr (next)', value: fmt(f.corrNext, 2) },
    { label: 'N days', value: int(f.nDays) },
    { label: 'Local-ccy edge left %', value: pct1(f.localCcyEdgeLeftPct) },
    { label: 'Alt future', value: text(f.altFuture) },
    { label: 'Alt expected edge %', value: pct1(f.altExpectedEdgePct) },
    { label: 'Alt corr (next)', value: fmt(f.altCorrNext, 2) },
    { label: 'β same', value: fmt(f.betaSame, 2) },
    { label: 'β next', value: fmt(f.betaNext, 2) },
    { label: 'Drops: bad print', value: int(drops?.BadPrint) },
    { label: 'Drops: ex-div', value: int(drops?.ExDiv) },
    { label: 'Drops: repeat', value: int(drops?.Repeat) },
    { label: 'Drops: no price', value: int(drops?.NoPrice) },
    { label: 'Unchanged NAV share', value: share(f.unchangedNavShare) },
    { label: 'As of', value: text(f.asOf) },
  ]

  const flags = orderedFlags(f.flags ?? [])
  const B: MeasureRow[] = flags.length === 0
    ? [{ label: 'Flags', value: 'none' }]
    : flags.map(fl => ({ label: fl, value: FLAG_LEGEND[fl] ?? DASH }))

  const C: MeasureRow[] = [
    { label: 'Size £m', value: fmt(f.sizeGbpM, 1) },
    { label: 'OCF %', value: fmt(f.ocfPct, 2) },
    { label: 'Levy', ...scalarOrJson(f.levy) },
    { label: 'Fair value', ...scalarOrJson(f.fairValue) },
    { label: 'HL page', ...scalarOrJson(f.hlUrl) },
    { label: 'Production ref', ...scalarOrJson(facts?.production_ref) },
  ]
  // A raw fact is skipped from the generic rows only when its dedicated row displayed it as a plain scalar;
  // anything else (objects, several sources, ...) falls through to the generic rows so nothing is lost.
  // production_ref has its own "Production ref" row; notes is shown in A.
  const skip = new Set(['production_ref', 'notes'])
  if (shownScalar(f.valuationTimeUk)) skip.add('valuation_time_uk')
  if (num(f.ocfPct) !== null) skip.add('ocf_pct')
  if (num(f.sizeGbpM) !== null) skip.add('size_gbp_m')
  const shownInside: Record<string, string[]> = { pricing_policy: [], hl: [] }
  if (shownScalar(f.levy)) shownInside.pricing_policy.push('levy')
  if (shownScalar(f.fairValue)) shownInside.pricing_policy.push('fair_value')
  if (shownScalar(f.hlUrl)) shownInside.hl.push('url')
  if (facts) {
    for (const [k, v] of Object.entries(facts)) {
      if (skip.has(k)) continue
      const inner = shownInside[k]
      const o = rec(v)
      if (inner && inner.length > 0 && o) {
        const rest = Object.fromEntries(Object.entries(o).filter(([kk]) => !inner.includes(kk)))
        if (Object.keys(rest).length === 0) continue
        C.push({ label: k, ...scalarOrJson(rest) })
        continue
      }
      C.push({ label: k, ...scalarOrJson(v) })
    }
  }

  const cs = rec(audit?.CrossSource)
  const nq = rec(audit?.NavQuality)
  const conc = rec(audit?.Concentration)
  const seg = rec(audit?.Segments)
  const largest = recs(nq?.LargestMoves)[0]
  const D: MeasureRow[] = [
    { label: 'Cross-source common', value: cs ? int(cs.Common) : DASH },
    { label: 'Cross-source only-primary', value: cs ? int(cs.OnlyInA) : DASH },
    { label: 'Cross-source only-secondary', value: cs ? int(cs.OnlyInB) : DASH },
    { label: 'Cross-source scale', value: cs ? fmt(cs.Scale, 4) : DASH },
    { label: 'Cross-source max rel gap', value: cs ? share(cs.MaxRelGap) : DASH },
    { label: 'Cross-source gaps >0.5%', value: cs ? int(cs.GapsOverHalfPct) : DASH },
    { label: 'NAV points', value: nq ? int(nq.Points) : DASH },
    { label: 'Unchanged share', value: nq ? share(nq.UnchangedShare) : DASH },
    { label: 'Long gaps', value: nq ? String(arr(nq.LongGaps).length) : DASH },
    { label: 'Largest move (log return)', value: largest ? `${text(largest.Date)} ${share(largest.LogReturn)}` : DASH },
    { label: 'Rule total (log-return sum)', value: conc ? share(conc.RuleTotal) : DASH },
    { label: 'Top-10 days (log-return sum)', value: conc ? share(conc.Top10) : DASH },
    { label: 'Top-50 days (log-return sum)', value: conc ? share(conc.Top50) : DASH },
    { label: 'Top-10 share of rule total', value: conc ? ratioShare(conc.Top10, conc.RuleTotal) : DASH },
    { label: 'Top-50 share of rule total', value: conc ? ratioShare(conc.Top50, conc.RuleTotal) : DASH },
    { label: 'Days in', value: conc ? int(conc.DaysIn) : DASH },
    { label: 'Segment R²', value: seg ? fmt(seg.R2, 2) : DASH },
    { label: 'Segment N', value: seg ? int(seg.N) : DASH },
    { label: 'Segment error', value: text(audit?.SegmentError) },
  ]
  const betas = arr(seg?.Betas), tstats = arr(seg?.TStats)
  const tables: MeasureTable[] = [
    {
      title: 'Per year', columns: ['Year', 'N', 'β next', 't', 'β same', 'Rule Σ log-ret %', 'Hold Σ log-ret %', 'Hit rate', 'Mean next bp (up)', 'Mean next bp (down)'],
      rows: recs(audit?.PerYear).map(r => [text(r.Year), int(r.N), fmt(r.BetaNext, 2), fmt(r.TNext, 2), fmt(r.BetaSame, 2),
        share(r.RuleSum), share(r.HoldSum), share(r.HitRateWhenIn), bp(r.MeanNextWhenUp), bp(r.MeanNextWhenDown)]),
    },
    {
      title: 'Periods', columns: ['From', 'To', 'N', 'β next', 't', 'β same'],
      rows: recs(audit?.Periods).map(r => [text(r.From), text(r.To), int(r.N), fmt(r.BetaNext, 2), fmt(r.TNext, 2), fmt(r.BetaSame, 2)]),
    },
    {
      title: 'Signal terciles', columns: ['Bucket', 'N', '|signal| bp', 'signed next bp', 'sign agree %'],
      rows: recs(audit?.Terciles).map(r => [text(r.Bucket), int(r.N), fmt(r.MeanAbsSignalBp, 1), fmt(r.MeanSignedNextBp, 1), fmt(r.SignAgreementPct, 1)]),
    },
    {
      title: 'Long NAV gaps', columns: ['Date', 'Gap days'],
      rows: recs(nq?.LongGaps).map(r => [text(r.Date), int(r.GapDays)]),
    },
    {
      title: 'Largest NAV moves (log return)', columns: ['Date', 'Log return'],
      rows: recs(nq?.LargestMoves).map(r => [text(r.Date), share(r.LogReturn)]),
    },
    {
      title: 'Segments', columns: ['Regressor', 'β', 't'],
      rows: seg ? SEGMENT_NAMES.map((name, i) => [name, fmt(betas[i], 2), fmt(tstats[i], 2)]) : [],
    },
  ]

  // Gate lines read "<key> (<name>): ...". Japan failures live in japanFailures, not failures.
  let gateValue = DASH
  const gateLabel = productionFund(f)?.toLowerCase()
  if (isLive && live.gate && gateLabel) {
    const lines = gateLines(live.gate).filter(x => x.toLowerCase().startsWith(gateLabel + ' '))
    gateValue = lines.length === 0 ? 'pass' : lines.join('; ')
  }
  const E: MeasureRow[] = [
    { label: 'Pricing RAG', value: text(ps?.Rag) },
    { label: 'Reason', value: text(ps?.Reason) },
    { label: 'β20', value: fmt(ps?.Beta20, 2) },
    { label: 'β40', value: fmt(ps?.Beta40, 2) },
    { label: 'β120', value: fmt(ps?.Beta120, 2) },
    { label: 'β250', value: fmt(ps?.Beta250, 2) },
    { label: 'Edge left 40', value: pct1(ps?.EdgeLeft40) },
    { label: 'Edge left 250', value: pct1(ps?.EdgeLeft250) },
    { label: 'Green ceiling', value: fmt(ps?.GreenCeiling, 2) },
    { label: 'Frozen ceiling', value: fmt(f.pricingCeiling, 2) },
    { label: 'Ceiling frozen', value: bool(ps?.CeilingFrozen) },
    { label: 'Red floor', value: fmt(ps?.RedFloor, 2) },
    { label: 'Plausible pairs', value: int(ps?.PlausiblePairs) },
    { label: 'Latest date', value: text(ps?.LatestDate) },
    { label: 'Ceiling set at', value: text(f.pricingCeilingSetAt) },
    { label: 'Gate', value: gateValue },
    { label: 'Not rankable', value: text(f.notRankableReason) },
  ]

  const notLive = '— (not live)'
  let rag = notLive, levy = notLive
  if (isLive) {
    rag = live.pricingRag ? `${live.pricingRag.rag}${live.pricingRag.reason ? ' — ' + live.pricingRag.reason : ''}` : DASH
    const l = live.levy
    levy = l
      ? `${l.checks} checks / ${l.suspected} suspected / ${l.mismatch} mismatch / last ${l.lastOutcome ?? DASH}${l.lastTradeDate ? ' ' + l.lastTradeDate : ''} / max adverse ${fmt(l.maxAdverseBp, 1)} bp`
      : DASH
  }
  const F: MeasureRow[] = [
    { label: 'Production RAG', value: rag },
    { label: 'Levy checks', value: levy },
  ]

  const G: MeasureRow[] = [
    { label: 'Status', value: f.status, hint: 'full change log below' },
    { label: 'Status reason', value: text(f.statusReason), hint: 'full change log below' },
  ]

  return [
    { key: 'A', title: 'Probe scores', rows: A },
    { key: 'B', title: 'Flags', rows: B },
    { key: 'C', title: 'Facts', rows: C },
    { key: 'D', title: 'Data & timing audit', rows: D, tables },
    { key: 'E', title: 'Gate & pricing source', rows: E },
    { key: 'F', title: 'Live cross-links', rows: F },
    { key: 'G', title: 'History', rows: G },
  ]
}
